// Modules
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import { DURATION_REGEX } from 'constants';
import { FormError, Form } from 'components/forms/form-structure';
import { SubmitButton } from 'components/widgets/buttons';
import { TimeReportDurationInput, TimeReportDescriptionInput } from 'components/forms/help-text';
import SVG from 'components/widgets/icons/svg';
import TimeReport from 'services/data/time-report';
import requiredValidation from 'utils/required-validation';

import MetadataFormSection from './metadata-form-section';


class TimeReportFormClass extends React.Component {
  static propTypes = {
    cancel: PropTypes.func,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    error: PropTypes.string,
    submitFailed: PropTypes.bool.isRequired,
    initialValues: PropTypes.object.isRequired,
    onSubmit: PropTypes.func,
    afterSubmit: PropTypes.func,
    eagerUpdate: PropTypes.func,
    resetOnSubmit: PropTypes.bool,
    reset: PropTypes.func,
    showRate: PropTypes.bool,
    editing: PropTypes.object,
    preventAutoFocus: PropTypes.bool
  };

  static defaultProps = {
    showRate: true
  };

  constructor() {
    super();
    this.state = {};

    this.onSubmit = this.onSubmit.bind(this);
  }

  onSubmit(values) {
    const { onSubmit, resetOnSubmit, eagerUpdate, editing, afterSubmit } = this.props;
    values.id = _.get(editing, 'id');

    if (eagerUpdate) eagerUpdate(values);

    return Promise.resolve()
      .then(() => {
        // If the user has passed in their own onSubmit use that
        if (onSubmit) return onSubmit(values);

        // If we have an id update the time report
        if (values.id) return TimeReport.update(values);

        // Create the new time report
        return TimeReport.create(values);
      })
      .then(timeReport => {
        if (resetOnSubmit) {
          this.props.reset();
          this.setState({ editingRate: false, editingDate: false, editingCreatedBy: false });
        }

        if (eagerUpdate) eagerUpdate(_.pick(timeReport, ['amount', 'id', 'rate']), false);
        if (afterSubmit) afterSubmit(timeReport);
      });
  }

  render() {
    const { cancel, submitting, error, submitFailed, handleSubmit, preventAutoFocus } = this.props;

    return (
      <div className="time-report-form">
        <div className="form-header">
          <SVG src="svg/time-report.svg" className="time-report" />
          <h5 className="form-title">Add Time Report</h5>
          {cancel && <SVG onClick={cancel} className="close" src="svg/close.svg" />}
        </div>
        <Form formRef={form => { this.formRef = form; }} onSubmit={handleSubmit(this.onSubmit)} customSubmitArea preventAutoFocus={preventAutoFocus}>
          <MetadataFormSection {...this.props} />
          <Field component={TimeReportDescriptionInput} props={{ form: this.formRef }} name="description" label="Description" autoComplete="off" />
          <Field component={TimeReportDurationInput} name="duration" label="Duration" />
          <div className="d-block m-0 mw-100 mb-3">
            <div>
              <h6>How to record drive time:</h6>
              <ol className="pl-4 mb-0">
                <li>Capture 1-way in your normal TR</li>
                <li>Capture 1-way in a second non-billable TR</li>
              </ol>
            </div>
          </div>
          <div className="submit-area">
            <SubmitButton showLoading={submitting} />
            <FormError error={error} submitFailed={submitFailed} />
          </div>
        </Form>
      </div>
    );
  }
}

const TimeReportForm = reduxForm({
  form: 'add-time-report',
  validate: (values) => {
    const errors = requiredValidation([
      'description',
      'duration',
      'date',
      'rate',
      'createdBy'
    ], values);

    if (values.rate === 0) delete errors.rate;
    if (Number(values.duration) === 0) errors.duration = 'Required';
    if (Number(values.duration) > 24) errors.duration = 'Create multiple time reports if you really worked more than 24 hrs';
    if (!DURATION_REGEX.test(values.duration)) errors.duration = 'Must be a multiple of 0.25';
    return errors;
  }
})(TimeReportFormClass);

export default TimeReportForm;

class DefaultTimeReportFormClass extends React.Component { // eslint-disable-line react/prefer-stateless-function
  render() {
    const { initialValues, currentUser, ...props } = this.props;
    const { ticket, agreement } = props;
    const defaultValues = { ...initialValues };

    if (_.isUndefined(defaultValues.date)) defaultValues.date = moment();
    if (_.isUndefined(defaultValues.agreement)) defaultValues.agreement = agreement.id;
    if (_.isUndefined(defaultValues.createdBy)) defaultValues.createdBy = currentUser.id;
    if (_.isUndefined(defaultValues.ticket)) defaultValues.ticket = ticket.id;
    if (_.isUndefined(defaultValues.afterHoursPremium)) defaultValues.afterHoursPremium = _.get(agreement, 'afterHoursPremium', 0);
    if (_.isUndefined(defaultValues.rate)) {
      const defaultRate = _.find(_.get(ticket, 'agreement.rates', []), { role: currentUser.billingRole });
      defaultValues.rate = ticket.timeDefaultBillable ? defaultRate.id : 0;
    }

    return <TimeReportForm {...props} initialValues={defaultValues} />;
  }
}

function mapStateToProps(state) {
  return {
    currentUser: state.auth.currentUser
  };
}

export const DefaultTimeReportForm = connect(mapStateToProps)(DefaultTimeReportFormClass);
